import { useEffect, useState } from "react";
import { PALETTE, FONT_DISPLAY, FONT_MONO, FONT_BODY, track } from "./tokens";
import { openCallMeNow } from "@/lib/openCallMeNow";
import { ArrowRight } from "./icons";
import { z } from "zod";

const CLOSE_LEAD_ENDPOINT =
  "https://dvddfvigkzikieuqrtcn.supabase.co/functions/v1/close-lead";
const CONTACT_EMAIL_ENDPOINT =
  "https://dvddfvigkzikieuqrtcn.supabase.co/functions/v1/send-contact-email";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR2ZGRmdmlna3ppa2lldXFydGNuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY4NzAxMTgsImV4cCI6MjA5MjQ0NjExOH0.DiSDka0k9Ig6jYjC1xiXfgiQO6KPNYh5BTUGfeZbfc8";

const ReserveSchema = z.object({
  name: z.string().trim().min(1, "Name required").max(100),
  company: z.string().trim().min(1, "Company required").max(120),
  email: z.string().trim().email("Valid email required").max(255),
  window: z.enum(["this-week", "next-week", "next-month"]),
});

type Props = {
  open: boolean;
  onClose: () => void;
  source?: string;
};

export const ReserveModal = ({ open, onClose, source = "cobrain-reserve" }: Props) => {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [win, setWin] = useState<"this-week" | "next-week" | "next-month">("this-week");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = ReserveSchema.safeParse({ name, company, email, window: win });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Check fields");
      return;
    }
    setSubmitting(true);
    try {
      const winLabel = parsed.data.window.replace("-", " ");
      const headers = {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      };

      // Primary: create the lead in Close. This is the source of truth.
      const leadRes = await fetch(CLOSE_LEAD_ENDPOINT, {
        method: "POST",
        headers,
        body: JSON.stringify({
          firstName: parsed.data.name,
          business: parsed.data.company,
          email: parsed.data.email,
          source,
          leadSource: "CoBrain Reserve",
          note: `Preferred install window: ${winLabel}`,
          pageUrl: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : "",
        }),
      });
      const leadJson = await leadRes.json().catch(() => ({}));
      if (!leadRes.ok || leadJson?.ok === false) {
        throw new Error(
          typeof leadJson?.error === "string"
            ? leadJson.error
            : "Could not save your reservation. Please try again or call us.",
        );
      }

      // Secondary, best-effort: notification/confirmation email.
      void fetch(CONTACT_EMAIL_ENDPOINT, {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: parsed.data.name,
          email: parsed.data.email,
          subject: `[${source}] CoBrain reserve · install ${winLabel}`,
          message: `Company: ${parsed.data.company}\nPreferred install window: ${winLabel}\nSource: ${source}`,
        }),
      }).catch(() => {});

      setSubmitted(true);
      track("reserve_submit", { window: win, source });
    } catch (err: any) {
      const msg = err?.message || err?.error?.message || "Could not save your reservation. Please try again or call us.";
      setError(msg);
      track("reserve_error", { window: win, source, error: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "12px 14px", borderRadius: 12,
    background: "#fff", border: `1px solid ${PALETTE.line}`,
    fontSize: 14.5, color: PALETTE.ink, fontFamily: FONT_BODY, outline: "none",
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
    color: PALETTE.muted, fontFamily: FONT_MONO, fontWeight: 700, marginBottom: 6, display: "block",
  };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 90,
      background: "rgba(23,21,19,0.6)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      animation: "cb-fade 220ms both",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: PALETTE.bg, color: PALETTE.ink, borderRadius: 24,
        padding: "clamp(28px, 4vw, 40px)", maxWidth: 520, width: "100%",
        border: `1px solid ${PALETTE.line}`,
        boxShadow: "0 40px 80px -20px rgba(0,0,0,0.45)",
        position: "relative", maxHeight: "90vh", overflowY: "auto",
      }}>
        <button type="button" onClick={onClose} aria-label="Close" style={{
          position: "absolute", top: 14, right: 14, background: "transparent", border: 0,
          cursor: "pointer", color: PALETTE.muted, fontSize: 22, lineHeight: 1, padding: 6,
        }}>×</button>

        {!submitted ? (
          <>
            <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: PALETTE.amber, fontWeight: 700, fontFamily: FONT_MONO, marginBottom: 12 }}>
              Reserve install
            </div>
            <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: 32, lineHeight: 1.1, margin: "0 0 10px", letterSpacing: "-0.015em" }}>
              Pick a window. <em style={{ color: PALETTE.amber }}>We'll handle the rest.</em>
            </h3>
            <p style={{ fontSize: 14.5, color: PALETTE.ink2, lineHeight: 1.55, margin: "0 0 22px" }}>
              30-minute mapping call · first workflow live within 24 hours of kickoff.
            </p>

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={labelStyle} htmlFor="cb-name">Name</label>
                <input id="cb-name" style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} required />
              </div>
              <div>
                <label style={labelStyle} htmlFor="cb-company">Company</label>
                <input id="cb-company" style={inputStyle} value={company} onChange={(e) => setCompany(e.target.value)} maxLength={120} required />
              </div>
              <div>
                <label style={labelStyle} htmlFor="cb-email">Email</label>
                <input id="cb-email" type="email" style={inputStyle} value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} required />
              </div>
              <div>
                <label style={labelStyle}>Install window</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[
                    { v: "this-week", l: "This week" },
                    { v: "next-week", l: "Next week" },
                    { v: "next-month", l: "Next month" },
                  ].map((opt) => {
                    const active = win === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setWin(opt.v as any)}
                        style={{
                          padding: "10px 16px", borderRadius: 999,
                          background: active ? PALETTE.ink : "#fff",
                          color: active ? "#fff" : PALETTE.ink2,
                          border: `1px solid ${active ? PALETTE.ink : PALETTE.line}`,
                          fontSize: 13, fontWeight: 600, cursor: "pointer",
                          fontFamily: FONT_BODY, transition: "background 160ms ease",
                        }}
                      >{opt.l}</button>
                    );
                  })}
                </div>
              </div>

              {error && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                  <div style={{ fontSize: 13, color: "#b03a2e", flex: 1, minWidth: 200 }}>{error}</div>
                  <button
                    type="button"
                    onClick={() => {
                      track("reserve_to_call_me_now");
                      onClose();
                      openCallMeNow("cobrain_reserve_error");
                    }}
                    style={{
                      fontSize: 13, color: PALETTE.amber, background: "transparent", border: 0,
                      cursor: "pointer", fontWeight: 600, padding: 0, textDecoration: "underline",
                    }}
                  >
                    Talk to Us Now
                  </button>
                </div>
              )}

              <button type="submit" disabled={submitting} style={{
                marginTop: 4, padding: "14px 22px", borderRadius: 999,
                background: PALETTE.ink, color: "#fff", border: 0,
                fontSize: 14, fontWeight: 600, cursor: submitting ? "wait" : "pointer",
                minHeight: 48, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                opacity: submitting ? 0.7 : 1,
              }}>
                {submitting ? "Reserving…" : <>Reserve my install <ArrowRight size={15} /></>}
              </button>
              <p style={{ fontSize: 11.5, color: PALETTE.muted, margin: 0, lineHeight: 1.5 }}>
                We reply within one business day. No payment now.
              </p>
            </form>
          </>
        ) : (
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: PALETTE.amber, fontWeight: 700, fontFamily: FONT_MONO, marginBottom: 12 }}>
              You're on the calendar
            </div>
            <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: 32, lineHeight: 1.1, margin: "0 0 14px", letterSpacing: "-0.015em" }}>
              Want to grab a slot <em style={{ color: PALETTE.amber }}>now?</em>
            </h3>
            <p style={{ fontSize: 14.5, color: PALETTE.ink2, lineHeight: 1.55, margin: "0 0 22px" }}>
              We've got your details. If you'd like to lock in a 30-minute mapping call right away, pick a time below.
            </p>
            <button
              type="button"
              onClick={() => {
                track("reserve_to_call_me_now");
                onClose();
                openCallMeNow("cobrain_reserve_success");
              }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "14px 22px", background: PALETTE.amber, color: "#fff",
                border: 0, cursor: "pointer", fontFamily: "inherit",
                borderRadius: 999, fontSize: 14, fontWeight: 600,
                minHeight: 48, boxShadow: "0 12px 32px -10px rgba(196,130,58,0.55)",
              }}
            >
              Talk to Us Now <ArrowRight size={15} />
            </button>
            <button type="button" onClick={onClose} style={{
              display: "block", marginTop: 16, background: "transparent", border: 0,
              color: PALETTE.muted, fontSize: 13, cursor: "pointer", fontFamily: FONT_BODY,
              textDecoration: "underline",
            }}>I'll wait for your reply instead</button>
          </div>
        )}
      </div>
    </div>
  );
};